/**
 * Collapsing of the "Summary of Previous Fundings" table so it fits on the single G702 budget page.
 *
 * Kept free of component imports so it can be unit-tested directly.
 */

/**
 * Max previous-fundings rows that fit in the G702 page's Previous Fundings box without pushing the
 * page past one A4 landscape sheet. Calibrated against an exported PDF — re-check it whenever the
 * G702 page's fonts, paddings or Draw Summary rows change.
 */
export const PREVIOUS_FUNDINGS_MAX_ROWS = 16;

/** One row fewer when any row is asterisked, to leave room for the "* Not reviewed by CDCC" footnote. */
export const PREVIOUS_FUNDINGS_MAX_ROWS_WITH_FOOTNOTE = 15;

export interface PreviousFundingLike {
  reviewedByCDCC?: boolean | null;
}

/** Asterisked (not reviewed by CDCC) rows are never collapsed. */
export const isUnreviewedFunding = (pf: PreviousFundingLike) => pf.reviewedByCDCC === false;

/**
 * Groups the first `cutoff` fundings into runs between asterisked rows; asterisked rows and every
 * row past `cutoff` stay on their own. e.g. 1, 2, 3*, 4*, 5, 6, 7* → [1,2], [3*], [4*], [5,6], [7*].
 */
function groupLeadingRows<T extends PreviousFundingLike>(fundings: T[], cutoff: number): T[][] {
  const groups: T[][] = [];
  let run: T[] = [];
  const flushRun = () => {
    if (run.length > 0) groups.push(run);
    run = [];
  };

  fundings.forEach((pf, i) => {
    if (i < cutoff && !isUnreviewedFunding(pf)) {
      run.push(pf);
      return;
    }
    flushRun();
    groups.push([pf]);
  });
  flushRun();

  return groups;
}

/**
 * Collapses the fewest leading rows needed to fit `maxRows`, keeping the most recent rows itemized.
 * With no asterisked rows, N fundings become one "1-x" row plus the last 15 rows (N - x + 1 = 16);
 * with any asterisked row the limit drops to 15 to make room for the footnote.
 * Each returned group is one table row — a group of more than one funding renders as a collapsed range.
 *
 * When even collapsing every row doesn't fit (too many asterisked rows), the fully collapsed
 * result is returned and the table is left to overflow.
 */
export function collapsePreviousFundings<T extends PreviousFundingLike>(
  fundings: T[],
  maxRows = fundings.some(isUnreviewedFunding) ? PREVIOUS_FUNDINGS_MAX_ROWS_WITH_FOOTNOTE : PREVIOUS_FUNDINGS_MAX_ROWS,
): T[][] {
  let cutoff = 0;
  let groups = fundings.map(pf => [pf]);
  while (groups.length > maxRows && cutoff < fundings.length) {
    cutoff += 1;
    groups = groupLeadingRows(fundings, cutoff);
  }

  return groups;
}
